import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Gift } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

const DISCOUNT_CODE = "WELLNESS15";
const POPUP_DISMISSED_KEY = "umbo_discount_popup_dismissed";

const DiscountPopup = () => {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const dismissed = localStorage.getItem(POPUP_DISMISSED_KEY);
    if (dismissed) return;
    const timer = setTimeout(() => setOpen(true), 3000);
    return () => clearTimeout(timer);
  }, []);

  const handleClose = () => {
    setOpen(false);
    localStorage.setItem(POPUP_DISMISSED_KEY, "true");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Ingresa un email válido");
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase
        .from("newsletter_subscribers")
        .insert({ email: email.trim().toLowerCase() });

      if (error) {
        if (error.code === "23505") {
          toast.info("Ya estás suscrito. Tu código es: " + DISCOUNT_CODE);
          setSubmitted(true);
        } else {
          toast.error("Ocurrió un error. Intenta de nuevo.");
        }
      } else {
        setSubmitted(true);
        toast.success("¡Código de descuento activado!");
        // Track Lead event
        if (typeof window !== "undefined") {
          if ((window as any).fbq) {
            (window as any).fbq("init", "659264332303711", { em: email.trim().toLowerCase() });
            (window as any).fbq("track", "Lead", { content_name: "Newsletter Signup" });
          }
          if ((window as any).gtag) {
            (window as any).gtag("event", "generate_lead", { event_category: "newsletter" });
          }
        }
      }
    } catch {
      toast.error("Ocurrió un error. Intenta de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop */}
          <motion.div
            className="fixed inset-0 bg-foreground/60 backdrop-blur-sm z-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
          />

          {/* Modal */}
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none"
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
          >
            <div className="relative bg-card rounded-2xl border border-border shadow-2xl max-w-md w-full overflow-hidden pointer-events-auto">
              {/* Close */}
              <button
                onClick={handleClose}
                className="absolute top-4 right-4 z-10 text-muted-foreground hover:text-foreground transition-colors"
                aria-label="Cerrar"
              >
                <X className="w-5 h-5" />
              </button>

              {/* Header accent */}
              <div className="bg-primary px-6 py-8 text-center">
                <Gift className="w-10 h-10 text-primary-foreground mx-auto mb-3" />
                <h3 className="text-2xl font-heading font-bold text-primary-foreground">
                  15% de Descuento
                </h3>
                <p className="text-primary-foreground/80 text-sm mt-1">
                  en tu primera compra
                </p>
              </div>

              {/* Body */}
              <div className="p-6">
                {!submitted ? (
                  <>
                    <p className="text-muted-foreground text-sm text-center mb-5">
                      Suscríbete a nuestro newsletter y recibe tu código de descuento exclusivo.
                    </p>
                    <form onSubmit={handleSubmit} className="space-y-3">
                      <Input
                        type="email"
                        placeholder="tu@email.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        maxLength={255}
                        className="text-center"
                      />
                      <Button
                        type="submit"
                        className="w-full"
                        size="lg"
                        disabled={loading}
                      >
                        {loading ? "Enviando..." : "Obtener Mi Descuento"}
                      </Button>
                    </form>
                    <button
                      onClick={handleClose}
                      className="w-full text-center text-xs text-muted-foreground/60 mt-4 hover:text-muted-foreground transition-colors"
                    >
                      No, gracias
                    </button>
                  </>
                ) : (
                  <div className="text-center py-2">
                    <p className="text-foreground font-semibold mb-2">¡Tu código de descuento!</p>
                    <div className="bg-secondary rounded-xl py-4 px-6 mb-4">
                      <span className="text-2xl font-heading font-bold tracking-widest text-primary">
                        {DISCOUNT_CODE}
                      </span>
                    </div>
                    <p className="text-muted-foreground text-xs mb-4">
                      Aplica este código en tu carrito de compra.
                    </p>
                    <Button onClick={handleClose} variant="outline" className="w-full">
                      Ir a Comprar
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default DiscountPopup;
